import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, X, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function CancelSubscriptionModal({ isOpen, onClose, onCanceled, currentPlan }) {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: confirm, 2: success/redirect

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      
      if (response.data.redirect) {
        // Store redirect (like Google Play or Microsoft Store)
        window.open(response.data.url, '_blank');
        setStep(2);
      } else {
        // Direct cancellation (Stripe/web)
        setStep(2);
        onCanceled && onCanceled();
      }
    } catch (error) {
      console.error("Cancellation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }} onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>

          {step === 1 ? (
            <>
              {/* Confirmation Step */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                  <AlertTriangle className="h-8 w-8" style={{ color: '#f59e0b' }} />
                </div>
                <h2 className="text-xl font-bold mb-2" style={{ color: '#f5f5f5' }}>
                  Cancel Subscription?
                </h2>
                <p className="text-sm" style={{ color: '#a0aec0' }}>
                  Are you sure you want to cancel your {currentPlan} subscription? You'll lose access to:
                </p>
              </div>

              <ul className="space-y-2 mb-6 px-4">
                <li className="flex items-center gap-2 text-sm" style={{ color: '#ef4444' }}>
                  <span>✕</span> Voice transcription
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#ef4444' }}>
                  <span>✕</span> Error correction & tone changing
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#ef4444' }}>
                  <span>✕</span> Translation & read aloud
                </li>
              </ul>

              <p className="text-xs text-center mb-6" style={{ color: '#718096' }}>
                Your subscription will remain active until the end of your current billing period.
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  className="flex-1 py-3"
                  style={{ backgroundColor: '#374151', color: '#f5f5f5' }}
                >
                  Keep Subscription
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="flex-1 py-3"
                  style={{ backgroundColor: '#ef4444', color: '#fff' }}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Yes, Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Success Step */}
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                  <span className="text-3xl">✓</span>
                </div>
                <h2 className="text-xl font-bold mb-2" style={{ color: '#f5f5f5' }}>
                  Subscription Canceled
                </h2>
                <p className="text-sm mb-6" style={{ color: '#a0aec0' }}>
                  Your subscription has been successfully canceled. You'll have access until the end of your billing period.
                </p>
                <Button
                  onClick={onClose}
                  className="w-full py-3"
                  style={{ backgroundColor: '#6366f1', color: '#fff' }}
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
